import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, TriangleAlert, Undo2, Check } from 'lucide-react';
import * as tienda from '@/api/tienda.api';
import * as facturacion from '@/api/facturacion.api';
import type {
  Gasto,
  TipoDeGasto,
  RendimientoDePaca,
  DevolucionPorTalla,
  VentasDeUnAnuncio,
} from '@/api/tienda.api';
import { DataTable, type Columna } from '@/components/datos/DataTable';
import { ConDetalle } from '@/components/datos/DetailDrawer';
import { EntityLink } from '@/components/datos/EntityLink';
import { useAbrirDetalle } from '@/components/datos/contexto-detalle';
import { ModuleHeader, Kpi } from '@/components/datos/ModuleHeader';
import { VistasGuardadas } from '@/components/datos/VistasGuardadas';
import { FilterBarPeriodo } from '@/components/datos/FilterBarPeriodo';
import { rangoDe, PERIODOS, type PeriodoId } from '@/components/datos/periodo';
import { useFiltrosEnUrl } from '@/hooks/useFiltrosEnUrl';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/hooks/useToast';
import { dinero, numero, porcentaje, fecha, variacion } from '@/lib/formato-tienda';

/**
 * FINANZAS — cuánto entró, cuánto costó y cuánto queda.
 *
 * Maciel abrió la reunión con esto: *"cuánto yo tengo que tener para
 * mercancía, cuánto para gasto fijo… si no hay administración, no hay
 * escalamiento."*
 *
 * ═══ LA REGLA DE ESTA PANTALLA ═══
 *
 * Un número que no se sabe NO se rellena con cero. Cada vez que hay piezas
 * vendidas sin costo cargado, se dice — arriba, antes que el margen. Un margen
 * que trata el costo desconocido como cero convierte una pérdida en ganancia,
 * y es el número más peligroso que le podríamos enseñar a alguien que con eso
 * decide qué volver a comprar.
 *
 * ═══ Y POR QUÉ CADA NÚMERO SE ABRE ═══
 *
 * Los cuatro KPIs llevan a la tabla que los explica. Un tablero de números que
 * no se pueden abrir obliga a creerle o a ignorarlo; no hay tercera opción, y
 * la segunda es la que pasa siempre.
 */

const VISTAS = [
  { id: 'resumen', etiqueta: 'Resumen' },
  { id: 'gastos', etiqueta: 'Gastos' },
  { id: 'pacas', etiqueta: 'Rendimiento por compra' },
  { id: 'devoluciones', etiqueta: 'Tallas que vuelven' },
  { id: 'anuncios', etiqueta: 'De qué anuncio', ayuda: 'Qué trajo la publicidad' },
] as const;

/**
 * El período del control fino, traducido al que entiende la API.
 *
 * La API solo distingue `hoy`, `semana` y `mes` porque son los únicos que
 * necesita sin fechas. Cuando el control manda un rango explícito, este valor
 * es de relleno y no se usa — pero tiene que ir, porque el esquema lo exige.
 */
const A_PERIODO_API: Record<PeriodoId, 'hoy' | 'semana' | 'mes'> = {
  hoy: 'hoy',
  ayer: 'hoy',
  '7dias': 'semana',
  mes: 'mes',
  'mes-pasado': 'mes',
  personalizado: 'mes',
};

const iso = (d: Date) => d.toISOString();

export default function Finanzas() {
  return (
    <ConDetalle>
      <Contenido />
    </ConDetalle>
  );
}
function Contenido() {
  const { filtros, poner } = useFiltrosEnUrl(
    { vista: 'resumen', periodo: 'mes', desde: '', hasta: '' },
    { vista: VISTAS.map((v) => v.id), periodo: [...PERIODOS.map((p) => p.id), 'personalizado'] },
  );

  const periodoId = filtros.periodo as PeriodoId;
  const r = useMemo(
    () => rangoDe(periodoId, { desde: filtros.desde, hasta: filtros.hasta }),
    [periodoId, filtros.desde, filtros.hasta],
  );
  const api = A_PERIODO_API[periodoId];
  const actual = { desde: iso(r.actual.desde), hasta: iso(r.actual.hasta) };
  const anterior = { desde: iso(r.anterior.desde), hasta: iso(r.anterior.hasta) };

  const resumen = useQuery({
    queryKey: ['tienda', 'resumen', actual],
    queryFn: () => tienda.resumen(api, actual),
  });
  const resumenAnterior = useQuery({
    queryKey: ['tienda', 'resumen', anterior],
    queryFn: () => tienda.resumen(api, anterior),
  });
  const queda = useQuery({
    queryKey: ['tienda', 'cuanto-queda', actual],
    queryFn: () => tienda.cuantoQueda(api, actual),
  });
  const quedaAnterior = useQuery({
    queryKey: ['tienda', 'cuanto-queda', anterior],
    queryFn: () => tienda.cuantoQueda(api, anterior),
  });
  const valor = useQuery({
    queryKey: ['tienda', 'valor-inventario'],
    queryFn: () => tienda.valorDelInventario(),
  });
  // Lo confirmado sin cobrar NO depende del período: es plata que está fuera
  // ahora mismo, con el mensajero o con la clienta.
  const pendientes = useQuery({
    queryKey: ['tienda', 'por-cobrar'],
    queryFn: () => tienda.porCobrar(),
  });

  const a = resumen.data;
  const q = queda.data;
  const aAnt = resumenAnterior.data;
  const qAnt = quedaAnterior.data;

  const deltaDe = (ahora: number | undefined, antes: number | undefined) =>
    ahora == null || antes == null ? null : variacion(ahora, antes);

  return (
    <div className="space-y-5">
      <ModuleHeader
        titulo="Finanzas"
        subtitulo="Cuánto entró, cuánto costó y cuánto te queda de verdad."
        kpis={
          <>
            <Kpi
              titulo="Vendido"
              valor={dinero(a?.totalVendido)}
              pie={`${a?.ventas ?? 0} venta${(a?.ventas ?? 0) === 1 ? '' : 's'}`}
              delta={deltaDe(a?.totalVendido, aAnt?.totalVendido)}
              onClick={() => poner('vista', 'resumen')}
            />
            <Kpi
              titulo="Costo de lo vendido"
              valor={dinero(q?.costoDeLoVendido)}
              delta={deltaDe(q?.costoDeLoVendido, qAnt?.costoDeLoVendido)}
              bajarEsBueno
            />
            <Kpi
              titulo="Margen bruto"
              valor={dinero(q?.margenBruto)}
              pie={
                q && q.totalVendido > 0
                  ? `${porcentaje(q.margenBruto / q.totalVendido)} de lo vendido`
                  : undefined
              }
              delta={deltaDe(q?.margenBruto, qAnt?.margenBruto)}
            />
            <Kpi
              titulo="Te queda limpio"
              valor={dinero(q?.quedaLimpio)}
              pie={
                q
                  ? `después de ${dinero(q.gastosDelPeriodo)} de gastos`
                  : undefined
              }
              delta={deltaDe(q?.quedaLimpio, qAnt?.quedaLimpio)}
              tono={(q?.quedaLimpio ?? 0) < 0 ? 'malo' : 'bueno'}
              onClick={() => poner('vista', 'gastos')}
            />
          </>
        }
      >
        <FilterBarPeriodo
          periodo={periodoId}
          onCambiar={(p) => poner('periodo', p)}
          personalizado={{ desde: filtros.desde, hasta: filtros.hasta }}
          onPersonalizado={(v) => {
            poner('desde', v.desde);
            poner('hasta', v.hasta);
          }}
        />
      </ModuleHeader>

      {/* El hueco se dice ANTES que el margen, no en una nota al pie. */}
      {q && q.lineasSinCosto > 0 && (
        <Aviso
          tono="amarillo"
          icono={<TriangleAlert className="mt-0.5 h-5 w-5 shrink-0" />}
          titulo={`${q.lineasSinCosto} línea${q.lineasSinCosto === 1 ? '' : 's'} vendida${
            q.lineasSinCosto === 1 ? '' : 's'
          } sin costo cargado.`}
          accion={
            <Button size="sm" variant="outline" onClick={() => poner('vista', 'pacas')}>
              Ver de qué compra vienen
            </Button>
          }
        >
          El margen de arriba NO las cuenta, así que lo que ganaste es distinto de lo que dice.
          Reparte el costo de su compra, o cárgalo a mano en Productos.
        </Aviso>
      )}

      {a && a.piezasDevueltas > 0 && (
        <Aviso
          tono="azul"
          icono={<Undo2 className="mt-0.5 h-5 w-5 shrink-0" />}
          titulo={`${a.piezasDevueltas} pieza${a.piezasDevueltas === 1 ? '' : 's'} devuelta${
            a.piezasDevueltas === 1 ? '' : 's'
          } (${dinero(a.montoDevuelto)}).`}
          accion={
            <Button size="sm" variant="outline" onClick={() => poner('vista', 'devoluciones')}>
              Ver qué tallas
            </Button>
          }
        >
          Ya están restadas de los números de arriba: lo que volvió al almacén no se vendió.
        </Aviso>
      )}

      <VistasGuardadas
        vistas={VISTAS.map((v) => ({ ...v }))}
        activa={filtros.vista}
        onCambiar={(v) => poner('vista', v)}
      />

      {filtros.vista === 'resumen' && (
        <Resumen
          topVariantes={a?.topVariantes ?? []}
          cargando={resumen.isLoading}
          valor={valor.data}
          puntoDeEquilibrio={q?.puntoDeEquilibrio ?? null}
          gastos={q?.gastosDelPeriodo ?? 0}
          fijos={q?.gastosFijosDelPeriodo ?? 0}
          variables={q?.gastosVariablesDelPeriodo ?? 0}
          vendido={q?.totalVendido ?? 0}
          costoDeLoVendido={q?.costoDeLoVendido ?? 0}
          porCobrar={(pendientes.data ?? []).reduce((a, v) => a + (v.porCobrar ?? v.total), 0)}
        />
      )}
      {filtros.vista === 'gastos' && <Gastos />}
      {filtros.vista === 'pacas' && <Pacas />}
      {filtros.vista === 'devoluciones' && <Devoluciones periodo={api} rango={actual} />}
      {filtros.vista === 'anuncios' && <Anuncios periodo={api} rango={actual} />}
    </div>
  );
}

/** Un sobre. El número grande, y debajo de dónde sale. */
function Aparte({
  titulo,
  monto,
  pie,
  tono = 'normal',
}: {
  titulo: string;
  monto: number;
  pie: string;
  tono?: 'normal' | 'bueno' | 'malo';
}) {
  return (
    <div className="rounded-lg border border-surface-border bg-surface-muted p-3">
      <p className="text-xs text-slate-500">{titulo}</p>
      <p
        className={
          'mt-0.5 text-xl font-semibold tabular-nums ' +
          (tono === 'malo'
            ? 'text-red-600 dark:text-red-400'
            : tono === 'bueno'
              ? 'text-green-700 dark:text-green-400'
              : 'text-slate-900 dark:text-slate-100')
        }
      >
        {dinero(monto)}
      </p>
      <p className="mt-0.5 text-xs text-slate-400">{pie}</p>
    </div>
  );
}

function Aviso({
  tono,
  icono,
  titulo,
  children,
  accion,
}: {
  tono: 'amarillo' | 'azul';
  icono: React.ReactNode;
  titulo: string;
  children: React.ReactNode;
  accion?: React.ReactNode;
}) {
  const clases =
    tono === 'amarillo'
      ? 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200'
      : 'border-sky-200 bg-sky-50 text-sky-900 dark:border-sky-900/50 dark:bg-sky-950/30 dark:text-sky-200';
  return (
    <div className={`flex flex-wrap items-start gap-3 rounded-lg border px-4 py-3 ${clases}`}>
      {icono}
      <div className="min-w-0 flex-1 text-sm">
        <p className="font-medium">{titulo}</p>
        <p className="mt-0.5 opacity-90">{children}</p>
      </div>
      {accion}
    </div>
  );
}

/**
 * LO QUE MÁS SALE — agrupado por producto, con sus tallas dentro.
 *
 * Maciel no compra tallas sueltas: compra pacas de un modelo. Ver «vestido
 * rosado M · 4» y «vestido rosado L · 3» como dos filas distintas esconde que
 * ese modelo lleva 7 piezas y es el que hay que reponer.
 */
function Resumen({
  topVariantes,
  cargando,
  valor,
  puntoDeEquilibrio,
  gastos,
  fijos,
  variables,
  vendido,
  costoDeLoVendido,
  porCobrar,
}: {
  topVariantes: tienda.TopVariante[];
  cargando: boolean;
  valor: tienda.ValorDelInventario | undefined;
  puntoDeEquilibrio: number | null;
  gastos: number;
  fijos: number;
  variables: number;
  vendido: number;
  costoDeLoVendido: number;
  /** Lo confirmado que aún no se ha cobrado: no está en la mano todavía. */
  porCobrar: number;
}) {
  const [abierto, setAbierto] = useState<Record<string, boolean>>({});

  const porProducto = useMemo(() => {
    const mapa = new Map<
      string,
      { producto: string; sku: string | null; cantidad: number; total: number; tallas: tienda.TopVariante[] }
    >();
    for (const t of topVariantes) {
      const clave = t.sku ?? t.producto;
      const previo = mapa.get(clave);
      if (previo) {
        previo.cantidad += t.cantidad;
        previo.total += t.total;
        previo.tallas.push(t);
      } else {
        mapa.set(clave, {
          producto: t.producto,
          sku: t.sku,
          cantidad: t.cantidad,
          total: t.total,
          tallas: [t],
        });
      }
    }
    return [...mapa.values()].sort((x, y) => y.total - x.total).slice(0, 5);
  }, [topVariantes]);

  // ── Cómo se reparte lo que entró ─────────────────────────────────────────
  //
  // Fue su PRIMERA frase de necesidad: «cuánto yo tengo que tener para
  // mercancía, [apartando] para gasto fijo y [apartando] para [mí]».
  //
  // Finanzas contestaba cuánto QUEDÓ, que es otra pregunta. Esto contesta la
  // suya, y sin inventar porcentajes: para volver a tener lo que vendiste hay
  // que apartar lo que te costó; los gastos del período son los que son; lo
  // que sobra es tuyo. Los tres números salen de los mismos datos que ya
  // alimentan el margen — no hay una regla nueva que alguien tenga que creer.
  const reponer = costoDeLoVendido;
  const tuyo = vendido - reponer - gastos;
  const comeCapital = tuyo < 0;

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <section className="rounded-lg border border-surface-border bg-surface p-4 lg:col-span-2">
        <h3 className="font-semibold text-slate-900 dark:text-slate-100">
          Cómo se reparte lo que entró
        </h3>
        <p className="mt-0.5 text-xs text-slate-500">
          De {dinero(vendido)} que vendiste en este período.
        </p>

        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          <Aparte
            titulo="Para volver a comprar"
            monto={reponer}
            pie="lo que te costó lo que salió"
          />
          <Aparte titulo="Para los gastos" monto={gastos} pie="fijos prorrateados + variables" />
          <Aparte
            titulo={comeCapital ? 'Te falta' : 'Te queda a ti'}
            monto={Math.abs(tuyo)}
            pie={comeCapital ? 'estás poniendo de tu capital' : 'después de reponer y pagar'}
            tono={comeCapital ? 'malo' : 'bueno'}
          />
        </div>

        {porCobrar > 0 && (
          <p className="mt-3 border-t border-surface-border pt-3 text-xs text-amber-700 dark:text-amber-500">
            Ojo: {dinero(porCobrar)} de eso todavía no ha entrado — son ventas contra entrega sin
            cobrar. El reparto de arriba cuenta lo vendido, no lo que tienes en la mano.
          </p>
        )}

        {vendido === 0 && (
          <p className="mt-3 border-t border-surface-border pt-3 text-xs text-slate-500">
            Sin ventas en este período no hay nada que repartir. Los gastos corren igual.
          </p>
        )}
      </section>

      <section className="rounded-lg border border-surface-border bg-surface p-4">
        <h3 className="font-semibold text-slate-900 dark:text-slate-100">Lo que más sale</h3>
        {cargando ? (
          <p className="py-6 text-center text-sm text-slate-400">Cargando…</p>
        ) : porProducto.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-400">
            Todavía no hay ventas confirmadas en este período.
          </p>
        ) : (
          <ul className="mt-3 divide-y divide-surface-border">
            {porProducto.map((p) => {
              const clave = p.sku ?? p.producto;
              const desplegado = abierto[clave] ?? false;
              return (
                <li key={clave} className="py-2">
                  <button
                    type="button"
                    onClick={() => setAbierto((v) => ({ ...v, [clave]: !desplegado }))}
                    className="flex w-full items-center justify-between gap-3 text-left text-sm"
                  >
                    <span className="min-w-0 truncate text-slate-700 dark:text-slate-200">
                      {p.sku && <span className="font-mono text-xs text-slate-500">{p.sku} </span>}
                      {p.producto}
                      <span className="ml-1 text-xs text-slate-400">
                        {p.tallas.length} talla{p.tallas.length === 1 ? '' : 's'}
                      </span>
                    </span>
                    <span className="shrink-0 tabular-nums text-slate-900 dark:text-slate-100">
                      {p.cantidad} · {dinero(p.total)}
                    </span>
                  </button>
                  {desplegado && (
                    <ul className="mt-1 space-y-0.5 pl-4">
                      {p.tallas.map((t) => (
                        <li
                          key={t.productVariantId}
                          className="flex items-center justify-between text-xs text-slate-500"
                        >
                          <span>talla {t.talla}</span>
                          <span className="tabular-nums">
                            {t.cantidad} · {dinero(t.total)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="space-y-4">
        <div className="rounded-lg border border-surface-border bg-surface p-4">
          <h3 className="font-semibold text-slate-900 dark:text-slate-100">
            Plata parada en el almacén
          </h3>
          {valor ? (
            <>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-900 dark:text-slate-100">
                {dinero(valor.valorAlCosto)}
              </p>
              <p className="text-sm text-slate-500">
                {numero(valor.piezas)} piezas al costo
                {valor.variantesSinCosto > 0 && (
                  <>
                    {' · '}
                    <span className="text-amber-700 dark:text-amber-500">
                      {valor.variantesSinCosto} sin costo, no cuentan
                    </span>
                  </>
                )}
              </p>
            </>
          ) : (
            <p className="py-4 text-sm text-slate-400">Cargando…</p>
          )}
        </div>

        <div className="rounded-lg border border-surface-border bg-surface p-4">
          <h3 className="font-semibold text-slate-900 dark:text-slate-100">
            Lo que salió este período
          </h3>
          <dl className="mt-2 space-y-1 text-sm">
            <div className="flex justify-between">
              <dt className="text-slate-500">Gastos fijos (prorrateados)</dt>
              <dd className="tabular-nums text-slate-900 dark:text-slate-100">{dinero(fijos)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Gastos variables (enteros)</dt>
              <dd className="tabular-nums text-slate-900 dark:text-slate-100">
                {dinero(variables)}
              </dd>
            </div>
            <div className="flex justify-between border-t border-surface-border pt-1 font-medium">
              <dt className="text-slate-900 dark:text-slate-100">Total</dt>
              <dd className="tabular-nums text-slate-900 dark:text-slate-100">{dinero(gastos)}</dd>
            </div>
          </dl>
          {puntoDeEquilibrio != null ? (
            <p className="mt-3 border-t border-surface-border pt-3 text-sm text-slate-600 dark:text-slate-300">
              Para cubrirlos necesitas vender{' '}
              <strong className="text-slate-900 dark:text-slate-100">
                {dinero(puntoDeEquilibrio)}
              </strong>{' '}
              en este período.
            </p>
          ) : (
            <p className="mt-3 border-t border-surface-border pt-3 text-xs text-slate-500">
              El punto de equilibrio necesita ventas con costo conocido. Todavía no se puede
              calcular, y por eso no se inventa un número.
            </p>
          )}
        </div>
      </section>
    </div>
  );
}

/**
 * GASTOS — fijos y variables en la MISMA tabla.
 *
 * Son la misma pregunta («¿qué salió de la caja?») con distinta frecuencia. La
 * diferencia es cómo entran en la cuenta del período: el fijo se prorratea por
 * días, el variable entra entero el día que salió. Eso se dice en la columna,
 * no en dos pantallas separadas.
 */
function Gastos() {
  const toast = useToast();
  const qc = useQueryClient();
  const [nuevo, setNuevo] = useState(false);
  const [concepto, setConcepto] = useState('');
  const [monto, setMonto] = useState('');
  const [tipo, setTipo] = useState<TipoDeGasto>('FIJO');
  const [categoria, setCategoria] = useState('');
  const [aplicaItbis, setAplicaItbis] = useState(false);
  const [buscar, setBuscar] = useState('');
  // Solo se pregunta por el ITBIS de un gasto si el negocio factura con ITBIS.
  const { data: fiscal } = useQuery({
    queryKey: ['facturacion', 'estado'],
    queryFn: facturacion.estadoDeFacturacion,
  });
  const conItbis = (fiscal?.itbis ?? 0) > 0;

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['tienda', 'gastos'],
    queryFn: () => tienda.listarGastos({}),
  });
  const gastos = data ?? [];

  const crear = useMutation({
    mutationFn: () =>
      tienda.crearGasto({
        concepto: concepto.trim(),
        monto: Number(monto),
        tipo,
        categoria: categoria.trim() || null,
        aplicaItbis: conItbis && aplicaItbis,
      }),
    onSuccess: () => {
      toast.success('Gasto agregado');
      setConcepto('');
      setMonto('');
      setCategoria('');
      setAplicaItbis(false);
      setNuevo(false);
      void qc.invalidateQueries({ queryKey: ['tienda'] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo agregar'),
  });

  const marcarPagado = useMutation({
    mutationFn: (g: Gasto) => tienda.actualizarGasto(g.id, { pagado: !g.pagado }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['tienda'] }),
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo'),
  });

  const quitar = useMutation({
    mutationFn: (g: Gasto) => tienda.actualizarGasto(g.id, { isActive: false }),
    onSuccess: () => {
      toast.success('Gasto dado de baja. No se borra: deja de contar de aquí en adelante.');
      void qc.invalidateQueries({ queryKey: ['tienda'] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo'),
  });

  const mensual = gastos
    .filter((g) => g.tipo === 'FIJO' && g.isActive)
    .reduce((a, g) => a + g.monto, 0);
  const pendientes = gastos.filter((g) => g.isActive && !g.pagado);

  const q = buscar.trim().toLowerCase();
  const filas = gastos.filter(
    (g) => !q || `${g.concepto} ${g.categoria ?? ''}`.toLowerCase().includes(q),
  );

  const columnas: Columna<Gasto>[] = [
    {
      id: 'concepto',
      titulo: 'Concepto',
      fija: true,
      valor: (g) => g.concepto,
      celda: (g) => (
        <span
          className={
            g.isActive
              ? 'font-medium text-slate-900 dark:text-slate-100'
              : 'text-slate-400 line-through'
          }
        >
          {g.concepto}
        </span>
      ),
    },
    {
      id: 'tipo',
      titulo: 'Tipo',
      ancho: 'w-28',
      valor: (g) => g.tipo,
      celda: (g) =>
        g.tipo === 'FIJO' ? (
          <Badge variant="info" title="Se prorratea por los días del período">
            Fijo / mes
          </Badge>
        ) : (
          <Badge title="Entra entero el día que salió">Variable</Badge>
        ),
    },
    {
      id: 'categoria',
      titulo: 'Categoría',
      valor: (g) => g.categoria ?? '',
      celda: (g) => (
        <span className="text-slate-600 dark:text-slate-300">{g.categoria ?? '—'}</span>
      ),
    },
    {
      id: 'fecha',
      titulo: 'Fecha',
      valor: (g) => g.fecha,
      celda: (g) => <span className="text-slate-600 dark:text-slate-300">{fecha(g.fecha)}</span>,
    },
    {
      id: 'pagado',
      titulo: 'Pagado',
      valor: (g) => (g.pagado ? 'sí' : 'no'),
      celda: (g) =>
        g.pagado ? <Badge variant="success">Pagado</Badge> : <Badge variant="warning">Debe</Badge>,
    },
    {
      id: 'monto',
      titulo: 'Monto',
      numerica: true,
      valor: (g) => g.monto,
      celda: (g) => dinero(g.monto),
      total: (fs) => dinero(fs.filter((g) => g.isActive).reduce((a, g) => a + g.monto, 0)),
    },
  ];

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500">
          Los fijos suman <strong className="text-slate-900 dark:text-slate-100">{dinero(mensual)}</strong> al
          mes y se prorratean por los días del período. Los variables entran enteros el día que
          salieron.
        </p>
        <Button onClick={() => setNuevo(true)}>
          <Plus className="h-4 w-4" />
          Nuevo gasto
        </Button>
      </div>

      <DataTable
        filas={filas}
        columnas={columnas}
        idDe={(g) => g.id}
        cargando={isLoading}
        error={isError ? (error instanceof Error ? error.message : 'Error') : null}
        onReintentar={() => void refetch()}
        buscar={buscar}
        onBuscar={setBuscar}
        placeholderBuscar="Busca por concepto o categoría…"
        memoria="gastos"
        exportarComo="gastos"
        accionesDeFila={[
          {
            etiqueta: 'Marcar pagado',
            icono: <Check className="h-3.5 w-3.5" />,
            ocultaSi: (g) => g.pagado || !g.isActive,
            onClick: (g) => marcarPagado.mutate(g),
          },
          {
            etiqueta: 'Dar de baja',
            peligrosa: true,
            ocultaSi: (g) => !g.isActive,
            onClick: (g) => quitar.mutate(g),
          },
        ]}
        vacio={{
          titulo: 'Sin gastos cargados',
          descripcion: 'Sin ellos no se puede saber cuánto te queda de verdad.',
          accion: <Button onClick={() => setNuevo(true)}>Cargar el primero</Button>,
        }}
      />

      {pendientes.length > 0 && (
        <p className="mt-2 text-xs text-amber-700 dark:text-amber-500">
          {pendientes.length} gasto{pendientes.length === 1 ? '' : 's'} sin pagar por{' '}
          {dinero(pendientes.reduce((a, g) => a + g.monto, 0))}. Cuentan igual en «te queda
          limpio»: el compromiso ya existe.
        </p>
      )}

      <Modal open={nuevo} onClose={() => setNuevo(false)} title="Nuevo gasto">
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
              Qué es
            </label>
            <Select value={tipo} onChange={(e) => setTipo(e.target.value as TipoDeGasto)}>
              <option value="FIJO">Fijo — se repite todos los meses</option>
              <option value="VARIABLE">Variable — salió una vez</option>
            </Select>
            <p className="mt-1 text-xs text-slate-500">
              {tipo === 'FIJO'
                ? 'Se carga por mes y se reparte entre los días del período que estés mirando.'
                : 'Entra entero el día de hoy. La compra de mercancía va aquí.'}
            </p>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
              Concepto
            </label>
            <Input
              value={concepto}
              onChange={(e) => setConcepto(e.target.value)}
              placeholder={tipo === 'FIJO' ? 'Luz, nómina, alquiler…' : 'Compra de octubre, envíos…'}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
                {tipo === 'FIJO' ? 'Monto al mes' : 'Monto'}
              </label>
              <Input
                type="number"
                value={monto}
                onChange={(e) => setMonto(e.target.value)}
                placeholder="8000"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
                Categoría <span className="font-normal text-slate-400">(opcional)</span>
              </label>
              <Input
                value={categoria}
                onChange={(e) => setCategoria(e.target.value)}
                placeholder="Mercancía, local, transporte…"
              />
            </div>
          </div>
          {conItbis && (
            <label className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={aplicaItbis}
                onChange={(e) => setAplicaItbis(e.target.checked)}
              />
              <span>
                Lleva ITBIS
                <span className="block text-xs text-slate-500">
                  Su {fiscal!.itbis}% se resta del ITBIS cobrado, en «Facturas → Impuestos».
                </span>
              </span>
            </label>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setNuevo(false)}>
              Cancelar
            </Button>
            <Button
              onClick={() => crear.mutate()}
              disabled={!concepto.trim() || monto === '' || crear.isPending}
            >
              {crear.isPending ? 'Agregando…' : 'Agregar'}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

/**
 * QUÉ SUPLIDOR DEJA PLATA.
 *
 * Es la pregunta que decide la próxima compra. «Vendí RD$40,000 este mes» no
 * dice a quién volver a comprarle; «de la paca de Ana salieron RD$18,000 con
 * RD$9,000 de margen y todavía queda el 40 % sin vender» sí.
 */
function Pacas() {
  const abrir = useAbrirDetalle();
  const [buscar, setBuscar] = useState('');
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['tienda', 'pacas-rendimiento'],
    queryFn: () => tienda.rendimientoDePacas(),
  });

  const q = buscar.trim().toLowerCase();
  const filas = (data ?? []).filter(
    (p) => !q || `${p.nombre} ${p.suplidor ?? ''}`.toLowerCase().includes(q),
  );

  const columnas: Columna<RendimientoDePaca>[] = [
    {
      id: 'paca',
      titulo: 'Compra',
      fija: true,
      valor: (p) => p.nombre,
      celda: (p) => <EntityLink tipo="paca" id={p.pacaId} etiqueta={p.nombre} />,
    },
    {
      id: 'suplidor',
      titulo: 'Suplidor',
      valor: (p) => p.suplidor ?? '',
      celda: (p) => (
        <span className="text-slate-600 dark:text-slate-300">{p.suplidor ?? '—'}</span>
      ),
    },
    {
      id: 'invertido',
      titulo: 'Invertido',
      numerica: true,
      valor: (p) => p.invertido,
      celda: (p) => dinero(p.invertido),
      total: (fs) => dinero(fs.reduce((a, p) => a + p.invertido, 0)),
    },
    {
      id: 'vendido',
      titulo: 'Vendido',
      numerica: true,
      valor: (p) => p.vendido,
      celda: (p) => dinero(p.vendido),
      total: (fs) => dinero(fs.reduce((a, p) => a + p.vendido, 0)),
    },
    {
      id: 'margen',
      titulo: 'Margen',
      numerica: true,
      valor: (p) => p.margen,
      celda: (p) => (
        <span
          className={
            p.margen < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-700 dark:text-green-400'
          }
        >
          {dinero(p.margen)}
        </span>
      ),
      total: (fs) => dinero(fs.reduce((a, p) => a + p.margen, 0)),
    },
    {
      id: 'sin-costo',
      titulo: 'Sin costo',
      numerica: true,
      valor: (p) => p.piezasSinCosto,
      celda: (p) =>
        p.piezasSinCosto > 0 ? (
          <span
            title="El margen no cuenta estas piezas: su costo no se sabe"
            className="text-amber-600 dark:text-amber-500"
          >
            {p.piezasSinCosto}
          </span>
        ) : (
          <span className="text-slate-400">—</span>
        ),
    },
    {
      id: 'avance',
      titulo: 'Vendido de la compra',
      numerica: true,
      valor: (p) => p.porcentajeVendido,
      celda: (p) => (
        <span className="inline-flex items-center gap-2">
          <span className="h-1.5 w-16 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
            <span
              className="block h-full rounded-full bg-brand-600"
              style={{ width: `${Math.min(100, Math.round(p.porcentajeVendido * 100))}%` }}
            />
          </span>
          <span className="tabular-nums text-slate-600 dark:text-slate-300">
            {porcentaje(p.porcentajeVendido)}
          </span>
        </span>
      ),
    },
    {
      id: 'piezas',
      titulo: 'Piezas',
      numerica: true,
      ocultaPorDefecto: true,
      valor: (p) => p.piezas,
      celda: (p) => (
        <span className="tabular-nums text-slate-600 dark:text-slate-300">
          {p.piezasVendidas} de {p.piezas}
        </span>
      ),
    },
  ];

  return (
    <DataTable
      filas={filas}
      columnas={columnas}
      idDe={(p) => p.pacaId}
      cargando={isLoading}
      error={isError ? (error instanceof Error ? error.message : 'Error') : null}
      onReintentar={() => void refetch()}
      buscar={buscar}
      onBuscar={setBuscar}
      placeholderBuscar="Busca por compra o suplidor…"
      memoria="rendimiento-pacas"
      exportarComo="rendimiento-de-compras"
      onAbrirFila={(p) => abrir?.({ tipo: 'paca', id: p.pacaId, etiqueta: p.nombre })}
      vacio={{
        titulo: 'Todavía no hay compras',
        descripcion:
          'Cuando cargues una compra y repartas su costo, aquí se ve cuánto dejó cada suplidor.',
      }}
    />
  );
}

/**
 * QUÉ TALLAS VUELVEN.
 *
 * Maciel, en la reunión: *"las devoluciones son por cuestión de talla… la
 * marca trabaja M pero no es S-M-L, las tallas chinas corren distinto."*
 *
 * Una talla con tasa alta no es mala suerte: es una talla que no hay que
 * volver a comprar, o que hay que avisar en el chat antes de vender.
 */
function Devoluciones({
  periodo,
  rango,
}: {
  periodo: 'hoy' | 'semana' | 'mes';
  rango: { desde: string; hasta: string };
}) {
  const [buscar, setBuscar] = useState('');
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['tienda', 'devoluciones', rango],
    queryFn: () => tienda.devolucionesPorTalla(periodo, rango),
  });

  const q = buscar.trim().toLowerCase();
  const filas = (data ?? []).filter(
    (d) => !q || `${d.producto} ${d.sku ?? ''} ${d.talla}`.toLowerCase().includes(q),
  );

  const columnas: Columna<DevolucionPorTalla>[] = [
    {
      id: 'sku',
      titulo: 'Código',
      fija: true,
      ancho: 'w-24',
      valor: (d) => d.sku ?? '',
      celda: (d) => <span className="font-mono text-xs text-slate-500">{d.sku ?? '—'}</span>,
    },
    {
      id: 'producto',
      titulo: 'Producto',
      valor: (d) => d.producto,
      celda: (d) => (
        <span className="text-slate-900 dark:text-slate-100">{d.producto}</span>
      ),
    },
    {
      id: 'talla',
      titulo: 'Talla',
      ancho: 'w-20',
      valor: (d) => d.talla,
      celda: (d) => <Badge variant="info">{d.talla}</Badge>,
    },
    {
      id: 'vendidas',
      titulo: 'Vendidas',
      numerica: true,
      valor: (d) => d.vendidas,
      celda: (d) => <span className="tabular-nums">{d.vendidas}</span>,
    },
    {
      id: 'devueltas',
      titulo: 'Devueltas',
      numerica: true,
      valor: (d) => d.devueltas,
      celda: (d) => <span className="tabular-nums">{d.devueltas}</span>,
    },
    {
      id: 'tasa',
      titulo: 'Tasa',
      numerica: true,
      valor: (d) => d.tasa ?? 0,
      celda: (d) => {
        const tasa = d.tasa ?? 0;
        return (
          <Badge variant={tasa >= 0.3 ? 'danger' : 'warning'}>{Math.round(tasa * 100)}%</Badge>
        );
      },
    },
  ];

  return (
    <>
      <DataTable
        filas={filas}
        columnas={columnas}
        idDe={(d) => `${d.sku ?? ''}-${d.talla}`}
        cargando={isLoading}
        error={isError ? (error instanceof Error ? error.message : 'Error') : null}
        onReintentar={() => void refetch()}
        buscar={buscar}
        onBuscar={setBuscar}
        placeholderBuscar="Busca por producto, código o talla…"
        memoria="devoluciones"
        exportarComo="tallas-que-vuelven"
        vacio={{
          titulo: 'Ninguna devolución en este período',
          descripcion: 'Cuando vuelva una pieza, aquí se ve qué talla y con qué frecuencia.',
        }}
      />
      <p className="mt-2 text-xs text-slate-500">
        Una talla que vuelve mucho no es mala suerte. O no se vuelve a comprar, o hay que avisar
        del tallaje en el chat antes de vender. Al armar una venta te lo recordamos.
      </p>
    </>
  );
}

/**
 * DE QUÉ ANUNCIO VINO LA PLATA.
 *
 * ═══ LA PREGUNTA DE LA REUNIÓN ═══
 *
 * Le preguntaron si al invertir en publicidad podía medir cuánto retornaba. No
 * podía: invertía por el botón azul de Instagram y lo único que veía era el
 * flujo de mensajes.
 *
 * ═══ POR QUÉ NO HAY UN «ROI» ═══
 *
 * El sistema sabe qué ENTRÓ por cada anuncio, porque WhatsApp manda el id del
 * anuncio con el primer mensaje. Lo que COSTÓ cada anuncio vive en Meta, y
 * hasta que ella lo cargue como gasto no hay forma de dividir una cosa por la
 * otra.
 *
 * Así que no se divide. Se enseña lo vendido por anuncio y, al lado, lo que
 * ella cargó de publicidad en el período. Un ROI calculado contra un gasto que
 * nadie cargó sería un número inventado con cara de precisión — y con ese
 * número se decide dónde poner la plata del mes.
 */
function Anuncios({
  periodo,
  rango,
}: {
  periodo: 'hoy' | 'semana' | 'mes';
  rango: { desde: string; hasta: string };
}) {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['tienda', 'por-anuncio', rango],
    queryFn: () => tienda.ventasPorAnuncio(periodo, rango),
  });

  const gastos = useQuery({
    queryKey: ['tienda', 'gastos'],
    queryFn: () => tienda.listarGastos({}),
  });

  const filas = data ?? [];
  const dePago = filas.filter((f) => f.sourceId);
  const vendidoDePago = dePago.reduce((a, f) => a + f.vendido, 0);
  const margenDePago = dePago.reduce((a, f) => a + f.margen, 0);
  const sinCosto = filas.reduce((a, f) => a + f.piezasSinCosto, 0);

  // Lo que ella cargó como publicidad. Por categoría, que es como lo escribe.
  const gastoEnPublicidad = (gastos.data ?? [])
    .filter(
      (g) =>
        g.isActive &&
        `${g.categoria ?? ''} ${g.concepto}`.toLowerCase().match(/public|anunc|meta|instagram|ads/),
    )
    .reduce((a, g) => a + g.monto, 0);

  const columnas: Columna<VentasDeUnAnuncio>[] = [
    {
      id: 'anuncio',
      titulo: 'Anuncio',
      fija: true,
      valor: (f) => f.titulo ?? f.sourceId ?? '',
      celda: (f) =>
        f.sourceId ? (
          <div className="min-w-0">
            <p className="truncate text-slate-900 dark:text-slate-100">
              {f.titulo ?? 'Anuncio sin titular'}
            </p>
            <p className="truncate font-mono text-xs text-slate-400">{f.sourceId}</p>
          </div>
        ) : (
          <span className="text-slate-600 dark:text-slate-300">
            Sin anuncio <span className="text-xs text-slate-400">· llegaron por su cuenta</span>
          </span>
        ),
    },
    {
      id: 'ventas',
      titulo: 'Ventas',
      numerica: true,
      valor: (f) => f.ventas,
      celda: (f) => <span className="tabular-nums">{f.ventas}</span>,
      total: (fs) => numero(fs.reduce((a, f) => a + f.ventas, 0)),
    },
    {
      id: 'vendido',
      titulo: 'Vendido',
      numerica: true,
      valor: (f) => f.vendido,
      celda: (f) => dinero(f.vendido),
      total: (fs) => dinero(fs.reduce((a, f) => a + f.vendido, 0)),
    },
    {
      id: 'margen',
      titulo: 'Margen',
      numerica: true,
      valor: (f) => f.margen,
      celda: (f) => (
        <span
          className={
            f.margen < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-700 dark:text-green-400'
          }
        >
          {dinero(f.margen)}
        </span>
      ),
      total: (fs) => dinero(fs.reduce((a, f) => a + f.margen, 0)),
    },
    {
      id: 'sin-costo',
      titulo: 'Sin costo',
      numerica: true,
      ocultaPorDefecto: true,
      valor: (f) => f.piezasSinCosto,
      celda: (f) =>
        f.piezasSinCosto > 0 ? (
          <span className="text-amber-600 dark:text-amber-500">{f.piezasSinCosto}</span>
        ) : (
          <span className="text-slate-400">—</span>
        ),
    },
  ];

  return (
    <>
      <div className="mb-3 grid gap-3 sm:grid-cols-3">
        <Aparte
          titulo="Vendido por anuncios"
          monto={vendidoDePago}
          pie={`${dePago.length} anuncio${dePago.length === 1 ? '' : 's'} con ventas`}
        />
        <Aparte
          titulo="Margen de esas ventas"
          monto={margenDePago}
          pie="con el costo congelado"
          tono={margenDePago < 0 ? 'malo' : 'bueno'}
        />
        <Aparte
          titulo="Gastado en publicidad"
          monto={gastoEnPublicidad}
          pie={gastoEnPublicidad === 0 ? 'no has cargado ninguno' : 'de lo que cargaste'}
        />
      </div>

      <DataTable
        filas={filas}
        columnas={columnas}
        idDe={(f) => f.sourceId ?? 'organico'}
        cargando={isLoading}
        error={isError ? (error instanceof Error ? error.message : 'Error') : null}
        onReintentar={() => void refetch()}
        memoria="por-anuncio"
        exportarComo="ventas-por-anuncio"
        vacio={{
          titulo: 'Todavía no hay ventas atribuidas',
          descripcion:
            'Se llena solo cuando alguien te escribe pulsando «Enviar mensaje» en un anuncio de Meta. Las ventas de quien llega por su cuenta salen como «Sin anuncio».',
        }}
      />

      <p className="mt-2 text-xs text-slate-500">
        {gastoEnPublicidad === 0
          ? 'Carga lo que gastaste en publicidad como gasto variable y vas a poder compararlo con lo de arriba. No se calcula un retorno contra un gasto que nadie cargó.'
          : 'Compara el margen con lo gastado. El costo exacto de cada anuncio vive en Meta: aquí va lo que tú cargaste en el período.'}
      </p>
      {sinCosto > 0 && (
        <p className="mt-1 text-xs text-amber-700 dark:text-amber-500">
          {sinCosto} pieza{sinCosto === 1 ? '' : 's'} de estas ventas no tiene costo cargado: su
          margen no se puede calcular y el de arriba sale alto. Es la misma cuenta que el «margen
          bruto» de la tarjeta.
        </p>
      )}
    </>
  );
}
